import React, { useState, useEffect, useRef } from "react";
import { 
    Send, Plus, Ticket, CheckCircle, Clock, Search, 
    Paperclip, X, Shield, MessageSquare, FileText, 
    ChevronRight, AlertCircle, HelpCircle, BookOpen,
    Briefcase, Monitor, Users, CreditCard, Bell,
    MoreVertical, Download, Image as ImageIcon, Calendar, Trash2
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { helpDeskAPI, helpDeskWebSocket, helpDeskUtils } from "../services/employee.service";

// --- Styles & Fonts Injection ---
const GlobalStyles = () => (
    <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Roboto:wght@300;400;500;700&display=swap');
        
        .font-heading { font-family: 'Poppins', sans-serif; }
        .font-body { font-family: 'Roboto', sans-serif; }
        
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #f1f5f9; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
    `}</style>
);

// --- Mock Data ---
const MY_TICKETS = [
    { 
        id: "TKT-2024-001", 
        subject: "Discrepancy in October Salary Slip", 
        status: "Open", 
        priority: "High",
        lastUpdate: "10 mins ago", 
        createdDate: "2023-10-24",
        department: "Finance", 
        assignedTo: "Payroll Team",
        timeline: [
            { status: "Created", time: "10:30 AM", date: "Oct 24", active: true },
            { status: "Assigned", time: "10:45 AM", date: "Oct 24", active: true },
            { status: "In Progress", time: "11:00 AM", date: "Oct 24", active: true },
            { status: "Resolved", time: "-", date: "-", active: false },
        ]
    },
    { 
        id: "TKT-2024-002", 
        subject: "Request for Adobe Cloud License", 
        status: "Pending", 
        priority: "Medium",
        lastUpdate: "2 hours ago", 
        createdDate: "2023-10-23",
        department: "IT Assets", 
        assignedTo: "IT Support",
        timeline: [
            { status: "Created", time: "09:00 AM", date: "Oct 23", active: true },
            { status: "Assigned", time: "09:30 AM", date: "Oct 23", active: true },
            { status: "Approval Pending", time: "10:00 AM", date: "Oct 23", active: true },
            { status: "Resolved", time: "-", date: "-", active: false },
        ]
    },
    { 
        id: "TKT-2024-003", 
        subject: "Work from Home Request - Nov", 
        status: "Closed", 
        priority: "Low",
        lastUpdate: "1 day ago", 
        createdDate: "2023-10-20",
        department: "HR", 
        assignedTo: "Sarah Jenkins",
        timeline: [
            { status: "Created", time: "2:00 PM", date: "Oct 20", active: true },
            { status: "Approved", time: "4:00 PM", date: "Oct 20", active: true },
            { status: "Closed", time: "4:05 PM", date: "Oct 20", active: true },
        ]
    },
];

const INITIAL_CHATS = {
    "TKT-2024-001": [
        { id: 1, type: "me", contentType: "text", text: "Hi, I noticed a deduction labeled 'Unpaid Leave' but I was present on Oct 12th.", time: "10:30 AM" },
        { id: 2, type: "admin", contentType: "text", text: "Hello! We have received your query. Assigning this to the payroll team to verify attendance logs.", time: "10:35 AM" },
        { id: 3, type: "admin", contentType: "text", text: "Could you please attach your attendance log for that week if you have it locally?", time: "10:36 AM" },
    ]
};

// --- Components ---

const StatusBadge = ({ status }) => {
    let styles = "";
    switch(status) {
        case "Open": styles = "bg-green-100 text-green-700 border-green-200"; break;
        case "Pending": styles = "bg-yellow-100 text-yellow-700 border-yellow-200"; break;
        case "Closed": styles = "bg-gray-100 text-gray-600 border-gray-200"; break;
        default: styles = "bg-gray-100 text-gray-600";
    }
    return (
        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${styles}`}>
            {status}
        </span>
    );
};

export default function EmployeeHelpDesk() {
    // --- State ---
    const [viewMode, setViewMode] = useState("chat"); // 'chat' or 'create'
    const [tickets, setTickets] = useState(MY_TICKETS); // Initialize with mock data
    const [activeTicketId, setActiveTicketId] = useState(MY_TICKETS[0]?.id || null);
    const [chats, setChats] = useState(INITIAL_CHATS);
    const [inputValue, setInputValue] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(false); // Set to false since we're using mock data
    
    // Filter State
    const [showDateFilter, setShowDateFilter] = useState(false);
    const [filterDate, setFilterDate] = useState("");

    // New Ticket Form State
    const [newTicket, setNewTicket] = useState({
        subject: "",
        category: "HR",
        priority: "Medium",
        description: "",
        files: [] // Array of File objects
    });

    // Refs
    const chatEndRef = useRef(null);
    const chatFileInputRef = useRef(null);
    const createTicketFileInputRef = useRef(null); // Ref for "Raise Ticket" upload

    // --- Derived State ---
    const activeTicket = tickets.find(t => t.id === activeTicketId) || {};
    const activeMessages = chats[activeTicketId] || [];
    
    const filteredTickets = tickets.filter(t => {
        const matchesSearch = (t.subject || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
                              (t.id || '').toLowerCase().includes(searchQuery.toLowerCase());
        const matchesDate = filterDate ? (t.createdDate || '').startsWith(filterDate) : true;
        return matchesSearch && matchesDate;
    });

    // --- Effects ---
    useEffect(() => {
        initializeHelpDesk();
        return () => {
            helpDeskWebSocket.disconnect();
        };
    }, []);

    useEffect(() => {
        if (viewMode === "chat") {
            chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [activeMessages, viewMode, activeTicketId]);

    // --- API Functions ---
    const initializeHelpDesk = async () => {
        try {
            setLoading(true);
            
            // Initialize WebSocket connection
            helpDeskUtils.initializeWebSocket('employee');
            
            // Set up WebSocket event handlers
            helpDeskWebSocket.onMessage('ticket_update', handleTicketUpdate);
            helpDeskWebSocket.onMessage('new_message', handleNewMessage);
            helpDeskWebSocket.onMessage('typing_indicator', handleTypingIndicator);
            
            // Load initial data
            await loadMyTickets();
            
        } catch (error) {
            console.error('Failed to initialize help desk:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadMyTickets = async () => {
        try {
            const response = await helpDeskAPI.getMyTickets();
            
            if (response.data.success) {
                setTickets(response.data.data);
                
                // Set first ticket as active if none selected
                if (!activeTicketId && response.data.data.length > 0) {
                    setActiveTicketId(response.data.data[0].id);
                    loadTicketDetails(response.data.data[0].id);
                }
            }
        } catch (error) {
            console.error('Failed to load tickets:', error);
        }
    };

    const loadTicketDetails = async (ticketId) => {
        try {
            const response = await helpDeskAPI.getTicket(ticketId);
            
            if (response.data.success) {
                const ticket = response.data.data;
                setChats(prev => ({
                    ...prev,
                    [ticketId]: ticket.messages || []
                }));
                
                // Subscribe to ticket updates
                helpDeskWebSocket.subscribeToTicket(ticketId);
            }
        } catch (error) {
            console.error('Failed to load ticket details:', error);
        }
    };

    // --- WebSocket Event Handlers ---
    const handleTicketUpdate = (message) => {
        const { ticket_id, update_type, data } = message;
        
        if (update_type === 'status_change') {
            setTickets(prev => prev.map(ticket => 
                ticket.id === ticket_id 
                    ? { ...ticket, status: data.new_status, updated_at: new Date().toISOString() }
                    : ticket
            ));
        }
    };

    const handleNewMessage = (message) => {
        const messageData = message.data;
        setChats(prev => ({
            ...prev,
            [messageData.ticket_id]: [...(prev[messageData.ticket_id] || []), messageData]
        }));
        
        // Update ticket's last message time
        setTickets(prev => prev.map(ticket => 
            ticket.id === messageData.ticket_id 
                ? { ...ticket, last_message_at: messageData.created_at, updated_at: messageData.created_at }
                : ticket
        ));
    };

    const handleTypingIndicator = (message) => {
        // Handle typing indicators (could show "Admin is typing...")
        console.log('Typing indicator:', message);
    };

    // --- Handlers ---
    const handleSendMessage = async () => {
        if (!inputValue.trim() || !activeTicketId) return;
        
        try {
            await helpDeskAPI.sendMessage(activeTicketId, inputValue, "employee", false);
            setInputValue("");
            
            // Send typing indicator (stop typing)
            helpDeskWebSocket.sendTyping(activeTicketId, false);
            
        } catch (error) {
            console.error('Failed to send message:', error);
        }
    };

    const handleTicketSelect = async (ticketId) => {
        if (activeTicketId) {
            helpDeskWebSocket.unsubscribeFromTicket(activeTicketId);
        }
        
        setActiveTicketId(ticketId);
        setViewMode("chat");
        await loadTicketDetails(ticketId);
    };

    const handleSubmitTicket = async () => {
        const errors = helpDeskUtils.validateTicketData(newTicket);
        if (errors.length > 0) {
            alert(errors.join('\n'));
            return;
        }

        try {
            // Get employee info from localStorage
            const empCode = localStorage.getItem('emp_code');
            const userName = localStorage.getItem('user_name') || 'Employee';
            const userEmail = localStorage.getItem('user_email') || 'employee@company.com';
            const userDept = localStorage.getItem('user_department') || 'General';
            
            const ticketData = {
                subject: newTicket.subject,
                description: newTicket.description,
                category: newTicket.category,
                priority: newTicket.priority,
                employee_name: userName,
                employee_email: userEmail,
                employee_department: userDept
            };
            
            const response = await helpDeskAPI.createTicket(ticketData);
            
            if (response.data.success) {
                const newTicketId = response.data.ticket_id;
                
                // Upload files if any
                for (const file of newTicket.files) {
                    await helpDeskAPI.uploadFile(newTicketId, file, "employee");
                }
                
                // Reset form
                setNewTicket({ subject: "", category: "HR", priority: "Medium", description: "", files: [] });
                
                // Reload tickets and switch to chat view
                await loadMyTickets();
                setActiveTicketId(newTicketId);
                setViewMode("chat");
                await loadTicketDetails(newTicketId);
            }
        } catch (error) {
            console.error('Failed to create ticket:', error);
            alert('Failed to create ticket. Please try again.');
        }
    };

    // Chat File Upload Handler
    const handleChatFileSelect = async (e) => {
        if (e.target.files && e.target.files[0] && activeTicketId) {
            const file = e.target.files[0];
            try {
                await helpDeskAPI.uploadFile(activeTicketId, file, "employee");
            } catch (error) {
                console.error('Failed to upload file:', error);
            }
        }
    };

    // Create Ticket File Upload Handler
    const handleCreateTicketFileSelect = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setNewTicket(prev => ({
                ...prev,
                files: [...prev.files, file]
            }));
        }
    };

    const removeAttachedFile = (index) => {
        setNewTicket(prev => ({
            ...prev,
            files: prev.files.filter((_, i) => i !== index)
        }));
    };

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
        
        // Send typing indicator
        if (activeTicketId) {
            helpDeskWebSocket.sendTyping(activeTicketId, e.target.value.length > 0);
        }
    };

    const formatMessageTime = (timestamp) => {
        return new Date(timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    };

    // --- Sub-Components ---
    const CategoryCard = ({ icon: Icon, label, active, onClick }) => (
        <div 
            onClick={onClick}
            className={`cursor-pointer p-4 rounded-xl border transition-all duration-200 flex flex-col items-center gap-2 text-center
            ${active 
                ? "bg-indigo-50 border-indigo-500 text-indigo-700 shadow-sm" 
                : "bg-white border-gray-200 text-gray-600 hover:border-indigo-300 hover:shadow-md"}`}
        >
            <Icon size={24} className={active ? "text-indigo-600" : "text-gray-400"} />
            <span className="text-xs font-medium font-body">{label}</span>
        </div>
    );

    return (
        <div className="flex h-screen w-full bg-[#f3f4f6] text-gray-800 overflow-hidden font-body">
            <GlobalStyles />
            {/* Hidden Input for Chat Attachments */}
            <input type="file" ref={chatFileInputRef} className="hidden" onChange={handleChatFileSelect} />
            {/* Hidden Input for New Ticket Attachments */}
            <input type="file" ref={createTicketFileInputRef} className="hidden" onChange={handleCreateTicketFileSelect} />


            {/* --- LEFT PANEL: My Tickets --- */}
            <div className="w-80 bg-white border-r border-gray-200 flex flex-col flex-shrink-0 z-10 shadow-sm">
                <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-indigo-50/50">
                    <div>
                        <h1 className="font-heading font-bold text-lg text-indigo-900">My Support</h1>
                        <p className="text-xs text-indigo-400 font-body">Help Desk Portal</p>
                    </div>
                    <button 
                        onClick={() => setViewMode("create")}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white p-2 rounded-lg shadow-md transition-all"
                        title="Raise New Ticket"
                    >
                        <Plus size={20} />
                    </button>
                </div>

                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 space-y-3">
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                            <input 
                                type="text" 
                                placeholder="Search tickets..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                            />
                        </div>
                        <button 
                            onClick={() => setShowDateFilter(!showDateFilter)}
                            className={`p-2 rounded-lg border transition-colors ${showDateFilter ? "bg-indigo-50 border-indigo-200 text-indigo-600" : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"}`}
                            title="Filter by Date"
                        >
                            <Calendar size={18} />
                        </button>
                    </div>

                    <AnimatePresence>
                        {showDateFilter && (
                            <motion.div 
                                initial={{ height: 0, opacity: 0 }} 
                                animate={{ height: 'auto', opacity: 1 }} 
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                            >
                                <div className="relative">
                                    <input 
                                        type="date"
                                        value={filterDate}
                                        onChange={(e) => setFilterDate(e.target.value)}
                                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 outline-none text-gray-600"
                                    />
                                    {filterDate && (
                                        <button 
                                            onClick={() => setFilterDate("")}
                                            className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                                        >
                                            <X size={14} />
                                        </button>
                                    )}
                                </div>
                                <p className="text-[10px] text-gray-400 mt-1 pl-1">Showing tickets from {filterDate || "all time"}</p>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                    {filteredTickets.length > 0 ? filteredTickets.map(ticket => (
                        <div 
                            key={ticket.id}
                            onClick={() => { setActiveTicketId(ticket.id); setViewMode("chat"); }}
                            className={`p-3 rounded-xl cursor-pointer border transition-all duration-200 group relative overflow-hidden ${
                                activeTicketId === ticket.id && viewMode === "chat"
                                ? "bg-white border-indigo-500 shadow-md" 
                                : "bg-white border-transparent hover:bg-white hover:border-gray-300 hover:shadow-sm"
                            }`}
                        >
                            {activeTicketId === ticket.id && viewMode === "chat" && (
                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-500"></div>
                            )}
                            
                            <div className="flex justify-between items-start mb-1 pl-2">
                                <span className={`font-heading font-semibold text-sm line-clamp-1 ${activeTicketId === ticket.id && viewMode === "chat" ? "text-indigo-900" : "text-gray-700"}`}>
                                    {ticket.subject}
                                </span>
                            </div>
                            
                            <div className="flex items-center gap-2 mb-2 pl-2">
                                <StatusBadge status={ticket.status} />
                                <span className="text-[10px] text-gray-400">#{ticket.id}</span>
                            </div>
                            
                            <div className="flex justify-between items-center pl-2 text-xs text-gray-500">
                                <span className="flex items-center gap-1"><Clock size={10} /> {ticket.lastUpdate}</span>
                                <span className="font-medium bg-gray-100 px-1.5 py-0.5 rounded text-[10px]">{ticket.department}</span>
                            </div>
                        </div>
                    )) : (
                        <div className="p-8 text-center text-gray-400 text-sm">
                            <Calendar className="w-8 h-8 mx-auto mb-2 opacity-20" />
                            <p>No tickets found for this date.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* --- CENTER PANEL: Workspace (Chat or Create) --- */}
            <div className="flex-1 flex flex-col bg-[#f8fafc] min-w-0 relative">
                
                {/* MODE A: CREATE NEW TICKET */}
                {viewMode === "create" && (
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                        className="flex-1 overflow-y-auto p-8"
                    >
                        <div className="max-w-2xl mx-auto">
                            <h2 className="font-heading text-2xl font-bold text-gray-800 mb-2">Raise a New Ticket</h2>
                            <p className="text-gray-500 mb-8 font-body">Please provide details about your issue so we can help you faster.</p>

                            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-6">
                                {/* Category Selection */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-3 font-heading">I need help with...</label>
                                    <div className="grid grid-cols-4 gap-3">
                                        {[
                                            { id: "HR", label: "HR & Policy", icon: Users },
                                            { id: "IT Assets", label: "IT Assets", icon: Monitor },
                                            { id: "Finance", label: "Payroll/Tax", icon: CreditCard },
                                            { id: "General", label: "General", icon: HelpCircle },
                                        ].map(cat => (
                                            <CategoryCard 
                                                key={cat.id} 
                                                {...cat} 
                                                active={newTicket.category === cat.id}
                                                onClick={() => setNewTicket({...newTicket, category: cat.id})}
                                            />
                                        ))}
                                    </div>
                                </div>

                                {/* Subject */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-heading">Subject</label>
                                    <input 
                                        type="text" 
                                        className="w-full p-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                        placeholder="e.g., Salary Slip discrepancy for Oct"
                                        value={newTicket.subject}
                                        onChange={(e) => setNewTicket({...newTicket, subject: e.target.value})}
                                    />
                                </div>

                                {/* Description */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-heading">Description</label>
                                    <textarea 
                                        className="w-full p-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all h-32 resize-none"
                                        placeholder="Describe your issue in detail..."
                                        value={newTicket.description}
                                        onChange={(e) => setNewTicket({...newTicket, description: e.target.value})}
                                    />
                                </div>

                                {/* Priority & Attachments */}
                                <div className="flex gap-4">
                                    <div className="w-1/2">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2 font-heading">Priority</label>
                                        <select 
                                            className="w-full p-3 border border-gray-200 rounded-lg text-sm outline-none bg-white"
                                            value={newTicket.priority}
                                            onChange={(e) => setNewTicket({...newTicket, priority: e.target.value})}
                                        >
                                            <option>Low</option>
                                            <option>Medium</option>
                                            <option>High</option>
                                            <option>Critical</option>
                                        </select>
                                    </div>
                                    <div className="w-1/2">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2 font-heading">Attachments</label>
                                        <div 
                                            onClick={() => createTicketFileInputRef.current.click()}
                                            className="border border-dashed border-gray-300 rounded-lg p-2.5 flex items-center justify-center gap-2 text-gray-500 cursor-pointer hover:bg-gray-50 transition-colors bg-white"
                                        >
                                            <Paperclip size={16} />
                                            <span className="text-sm">Upload File</span>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Selected Files List */}
                                {newTicket.files.length > 0 && (
                                    <div className="space-y-2 bg-gray-50 p-3 rounded-lg border border-gray-100">
                                        {newTicket.files.map((file, idx) => (
                                            <div key={idx} className="flex justify-between items-center bg-white p-2 rounded border border-gray-200 text-sm">
                                                <div className="flex items-center gap-2 overflow-hidden">
                                                    <FileText size={14} className="text-indigo-500 flex-shrink-0"/>
                                                    <span className="truncate text-gray-700">{file.name}</span>
                                                    <span className="text-gray-400 text-xs flex-shrink-0">({(file.size / 1024).toFixed(1)} KB)</span>
                                                </div>
                                                <button onClick={() => removeAttachedFile(idx)} className="text-red-400 hover:text-red-600 p-1">
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Action Buttons */}
                                <div className="pt-4 flex gap-3 justify-end border-t border-gray-100">
                                    <button 
                                        onClick={() => setViewMode("chat")}
                                        className="px-6 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        onClick={handleSubmitTicket}
                                        className="px-6 py-2.5 text-sm font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg shadow-md transition-colors flex items-center gap-2"
                                    >
                                        <Send size={16} /> Submit Ticket
                                    </button>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* MODE B: CHAT INTERFACE */}
                {viewMode === "chat" && (
                    <>
                        <div className="h-16 bg-white border-b border-gray-200 flex justify-between items-center px-6 shadow-sm flex-shrink-0">
                            <div>
                                <h2 className="font-heading font-semibold text-lg text-gray-800 truncate">
                                    {activeTicket?.subject || 'Select a ticket'}
                                </h2>
                                <div className="flex items-center gap-2 text-xs text-gray-500 font-body">
                                    <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded font-medium">{activeTicket?.department || 'N/A'}</span>
                                    <span>•</span>
                                    <span>Created {activeTicket?.createdDate || 'N/A'}</span>
                                </div>
                            </div>
                            {activeTicket?.status && <StatusBadge status={activeTicket.status} />}
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 space-y-4">
                            <AnimatePresence>
                                {activeMessages.map((msg) => (
                                    <motion.div 
                                        key={msg.id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className={`flex ${msg.type === "me" ? "justify-end" : msg.type === "system" ? "justify-center" : "justify-start"}`}
                                    >
                                        {msg.type === "system" ? (
                                            <div className="bg-gray-100 text-gray-500 text-xs px-4 py-1.5 rounded-full italic flex items-center gap-2">
                                                <Clock size={12} /> {msg.text}
                                            </div>
                                        ) : (
                                            <div className={`max-w-[70%]`}>
                                                <div className={`flex items-end gap-2 ${msg.type === "me" ? "flex-row-reverse" : ""}`}>
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shadow-sm flex-shrink-0
                                                        ${msg.type === "me" ? "bg-indigo-600" : "bg-gray-500"}`}>
                                                        {msg.type === "me" ? "ME" : "AD"}
                                                    </div>
                                                    
                                                    <div className={`p-4 rounded-2xl text-sm font-body shadow-sm leading-relaxed
                                                        ${msg.type === "me" 
                                                            ? "bg-indigo-600 text-white rounded-br-none" 
                                                            : "bg-white text-gray-800 border border-gray-200 rounded-bl-none"}`}>
                                                        
                                                        {msg.contentType === "file" && (
                                                            <div className="flex items-center gap-3 bg-black/10 p-2 rounded-lg mb-1">
                                                                <FileText size={20} />
                                                                <div className="text-xs">
                                                                    <p className="font-medium">{msg.fileName}</p>
                                                                    <p className="opacity-70">{msg.fileSize}</p>
                                                                </div>
                                                            </div>
                                                        )}
                                                        
                                                        {msg.text}
                                                        
                                                        <div className={`text-[10px] mt-2 text-right opacity-70 ${msg.type === "me" ? "text-indigo-100" : "text-gray-400"}`}>
                                                            {msg.time}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                            <div ref={chatEndRef} />
                        </div>

                        {/* Input Area */}
                        {activeTicket?.status !== "Closed" ? (
                            <div className="p-4 border-t border-gray-200 bg-white">
                                <div className="relative">
                                    <button 
                                        onClick={() => chatFileInputRef.current?.click()}
                                        className="absolute left-3 top-3 text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        <Paperclip size={18} />
                                    </button>
                                    
                                    <input
                                        value={inputValue}
                                        onChange={(e) => setInputValue(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                                        placeholder="Type your reply..."
                                        className="w-full pl-10 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                    />
                                    
                                    <button 
                                        onClick={handleSendMessage}
                                        disabled={!inputValue.trim()}
                                        className={`absolute right-2 top-2 p-1.5 rounded-lg transition-all ${
                                            inputValue.trim() 
                                            ? "bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm" 
                                            : "bg-gray-200 text-gray-400 cursor-not-allowed"
                                        }`}
                                    >
                                        <Send size={16} />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="p-4 border-t border-gray-200 bg-gray-50 text-center text-sm text-gray-500 font-medium flex items-center justify-center gap-2">
                                <CheckCircle size={16} className="text-green-500" /> This ticket is closed. Please raise a new ticket for further assistance.
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* --- RIGHT PANEL: Info & Context --- */}
            <div className="w-80 bg-white border-l border-gray-200 flex flex-col flex-shrink-0 z-10 shadow-sm hidden xl:flex">
                {viewMode === "create" ? (
                    // SUGGESTIONS PANEL (For Create Mode)
                    <div className="flex flex-col h-full">
                        <div className="p-5 border-b border-gray-100">
                            <h3 className="font-heading font-semibold text-gray-800 flex items-center gap-2">
                                <BookOpen size={18} className="text-indigo-600"/> Suggested Help
                            </h3>
                        </div>
                        <div className="p-5 flex-1 overflow-y-auto space-y-4">
                            <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                                <div className="flex gap-2 items-start text-blue-800">
                                    <AlertCircle size={16} className="mt-0.5 flex-shrink-0"/>
                                    <div>
                                        <p className="text-xs font-bold font-heading mb-1">Before you ask...</p>
                                        <p className="text-xs leading-relaxed opacity-90">Did you check the holiday calendar? Many salary discrepancies are due to unpaid holidays.</p>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Popular Articles</h4>
                                <ul className="space-y-3">
                                    {["Understanding your Payslip", "How to claim LTA", "VPN Troubleshooting Guide", "Leave Policy 2024"].map((item, i) => (
                                        <li key={i} className="flex items-center gap-2 text-sm text-gray-600 hover:text-indigo-600 cursor-pointer group transition-colors">
                                            <FileText size={14} className="text-gray-400 group-hover:text-indigo-500"/>
                                            <span className="border-b border-transparent group-hover:border-indigo-200">{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                ) : (
                    // TICKET INFO PANEL (For Chat Mode)
                    <div className="flex flex-col h-full">
                        <div className="p-5 border-b border-gray-100 bg-gray-50/50">
                            <h3 className="font-heading font-semibold text-gray-800 text-sm uppercase tracking-wide text-center">Ticket Status</h3>
                        </div>
                        
                        <div className="p-6 flex-1 overflow-y-auto">
                            {/* Timeline */}
                            <div className="relative pl-4 border-l-2 border-gray-100 space-y-8 mb-8">
                                {(activeTicket?.timeline || []).map((event, idx) => (
                                    <div key={idx} className="relative">
                                        <div className={`absolute -left-[21px] top-0 w-3.5 h-3.5 rounded-full border-2 
                                            ${event.active ? "bg-green-500 border-green-100" : "bg-gray-200 border-white"}`}></div>
                                        <div>
                                            <p className={`text-sm font-semibold ${event.active ? "text-gray-800" : "text-gray-400"}`}>{event.status}</p>
                                            {event.time !== "-" && (
                                                <p className="text-xs text-gray-500 mt-0.5">{event.date} • {event.time}</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Details Card */}
                            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 space-y-3">
                                <div>
                                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Assigned Agent</p>
                                    <div className="flex items-center gap-2 mt-1">
                                        <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-[10px] font-bold">
                                            {(activeTicket?.assignedTo || 'N')[0]}
                                        </div>
                                        <p className="text-sm font-medium text-gray-700">{activeTicket?.assignedTo || 'Not assigned'}</p>
                                    </div>
                                </div>
                                <div className="border-t border-gray-200 pt-3">
                                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Expected Resolution</p>
                                    <p className="text-sm font-medium text-gray-700 mt-1">Within 24 Hours</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}